import React, { useMemo, useState } from 'react'
import { Alert, Button, Icon, Input, message, Modal, Select, Tag } from 'antd'

const { Option } = Select

const IDENTIFIER_KEYS = new Set([
  'id',
  'key',
  'scene_order',
  'shot_order',
  'sceneOrder',
  'shotOrder',
  'shots'
])

const FIELD_ALIASES = {
  'initial image prompt': 'initial_image_prompt',
  initialImagePrompt: 'initial_image_prompt',
  image_prompt: 'initial_image_prompt',
  imagePrompt: 'initial_image_prompt',
  'video prompt': 'video_prompt',
  videoPrompt: 'video_prompt',
  'voiceover text': 'voiceover_text',
  voiceoverText: 'voiceover_text'
}

const EMPTY_TEMPLATE_FIELDS = {
  dialogue: {},
  initial_image_prompt: '',
  video_prompt: ''
}

const normalizeKey = value =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const getSceneOrder = scene => scene?.scene_order || scene?.sceneOrder
const getShotOrder = shot => shot?.shot_order || shot?.shotOrder

const getPatchScenes = data => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.scenes)) return data.scenes
  if (Array.isArray(data?.shotList)) return data.shotList
  return []
}

const normalizeFieldName = key => FIELD_ALIASES[key] || key

const isEmptyPatchValue = value => {
  if (value === undefined || value === null || value === '') return true
  if (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  ) {
    return true
  }
  return false
}

const getPatchFields = shot =>
  Object.entries(shot || {}).reduce((acc, [key, value]) => {
    if (IDENTIFIER_KEYS.has(key)) return acc
    if (isEmptyPatchValue(value)) return acc
    acc[normalizeFieldName(key)] = value
    return acc
  }, {})

const findSceneIndex = (scenes, patchScene) => {
  const sceneOrder = Number(getSceneOrder(patchScene))
  if (Number.isFinite(sceneOrder) && sceneOrder > 0) {
    const index = sceneOrder - 1
    if (scenes[index]) return index
  }

  const targetId = normalizeKey(patchScene?.id || patchScene?.key)
  if (!targetId) return -1

  return scenes.findIndex(scene => {
    const sceneId = normalizeKey(scene?.id || scene?.key || scene?.title)
    return sceneId === targetId || sceneId.endsWith(targetId)
  })
}

const findShotIndex = (shots, patchShot) => {
  const shotOrder = Number(getShotOrder(patchShot))
  if (Number.isFinite(shotOrder) && shotOrder > 0) {
    const index = shotOrder - 1
    if (shots[index]) return index
  }

  const targetId = normalizeKey(patchShot?.id || patchShot?.key)
  if (!targetId) return -1

  return shots.findIndex(shot => {
    const shotId = normalizeKey(shot?.id || shot?.key || shot?.title)
    return shotId === targetId || shotId.endsWith(targetId)
  })
}

const buildTemplate = (scenes, mode) => ({
  scenes: (scenes || []).map((scene, sceneIndex) => ({
    id: scene.id || `scene ${sceneIndex + 1}`,
    scene_order: sceneIndex + 1,
    shots: (scene.shots || []).map((shot, shotIndex) => ({
      id: shot.key || `shot ${shotIndex + 1}`,
      shot_order: shotIndex + 1,
      ...(mode === 'current'
        ? {
            dialogue: shot.dialogue || {},
            initial_image_prompt: shot.initial_image_prompt || '',
            video_prompt: shot.video_prompt || ''
          }
        : EMPTY_TEMPLATE_FIELDS)
    }))
  }))
})

const applyPatchToScenes = (scenes, patchData) => {
  const nextScenes = (scenes || []).map(scene => ({
    ...scene,
    shots: (scene.shots || []).map(shot => ({ ...shot }))
  }))
  const changes = []
  const misses = []

  getPatchScenes(patchData).forEach(patchScene => {
    const sceneIndex = findSceneIndex(nextScenes, patchScene)

    if (sceneIndex === -1) {
      misses.push(`Scene ${patchScene?.id || getSceneOrder(patchScene)}`)
      return
    }

    const scene = nextScenes[sceneIndex]

    ;(patchScene.shots || []).forEach(patchShot => {
      const shotIndex = findShotIndex(scene.shots || [], patchShot)

      if (shotIndex === -1) {
        misses.push(
          `Scene ${sceneIndex + 1}, shot ${
            patchShot?.id || getShotOrder(patchShot)
          }`
        )
        return
      }

      const fields = getPatchFields(patchShot)
      const fieldNames = Object.keys(fields)

      if (!fieldNames.length) return

      scene.shots[shotIndex] = {
        ...scene.shots[shotIndex],
        ...fields
      }

      changes.push({
        scene: sceneIndex + 1,
        shot: shotIndex + 1,
        fields: fieldNames
      })
    })
  })

  return { scenes: nextScenes, changes, misses }
}

const copyText = text => {
  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    navigator.clipboard.writeText
  ) {
    return navigator.clipboard.writeText(text)
  }

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
  return Promise.resolve()
}

const LocalShotJsonPatchModal = ({ visible, scenes = [], onClose, onApply }) => {
  const [jsonText, setJsonText] = useState('')
  const [templateMode, setTemplateMode] = useState('empty')

  const templateText = useMemo(
    () => JSON.stringify(buildTemplate(scenes, templateMode), null, 2),
    [scenes, templateMode]
  )

  const parsedPatch = useMemo(() => {
    if (!jsonText.trim()) return { data: null, error: null }

    try {
      const data = JSON.parse(jsonText)
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  }, [jsonText])

  const preview = useMemo(() => {
    if (!parsedPatch.data) return { changes: [], misses: [], scenes }
    return applyPatchToScenes(scenes, parsedPatch.data)
  }, [parsedPatch.data, scenes])

  const handleCopyTemplate = () => {
    copyText(templateText)
      .then(() => message.success('JSON template copied.'))
      .catch(() => message.error('Could not copy template.'))
  }

  const handleApply = () => {
    if (!parsedPatch.data || parsedPatch.error) {
      message.error('Paste valid JSON before applying.')
      return
    }

    if (!preview.changes.length) {
      message.warning('No matching shot fields were found to update.')
      return
    }

    onApply && onApply(preview.scenes)
    message.success(`Updated ${preview.changes.length} shot entries.`)
    onClose && onClose()
  }

  return (
    <Modal
      title="Local Shot JSON Patch"
      visible={visible}
      onCancel={onClose}
      width={980}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="apply"
          type="primary"
          icon="check"
          disabled={!parsedPatch.data || Boolean(parsedPatch.error)}
          onClick={handleApply}
        >
          Apply JSON Patch
        </Button>
      ]}
    >
      <Alert
        type="info"
        showIcon
        message="Local-only shot updater"
        description="Paste JSON with scenes and shots. Scenes match by scene_order or id, shots match by shot_order or id. Any shot field you include will be merged into that shot."
        style={{ marginBottom: 16 }}
      />

      <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8
            }}
          >
            <strong>Template</strong>
            <div style={{ display: 'flex', gap: 8 }}>
              <Select
                size="small"
                value={templateMode}
                onChange={setTemplateMode}
                style={{ width: 150 }}
              >
                <Option value="empty">Blank fields</Option>
                <Option value="current">Current values</Option>
              </Select>
              <Button size="small" icon="copy" onClick={handleCopyTemplate}>
                Copy JSON
              </Button>
              <Button
                size="small"
                icon="export"
                onClick={() => setJsonText(templateText)}
              >
                Use
              </Button>
            </div>
          </div>
          <Input.TextArea
            value={templateText}
            readOnly
            autosize={{ minRows: 18, maxRows: 18 }}
            style={{ fontFamily: 'monospace', fontSize: 12 }}
          />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8
            }}
          >
            <strong>Paste JSON</strong>
            <Button
              size="small"
              icon="rollback"
              disabled={!jsonText}
              onClick={() => setJsonText('')}
            >
              Clear
            </Button>
          </div>
          <Input.TextArea
            value={jsonText}
            onChange={event => setJsonText(event.target.value)}
            placeholder="Paste generated JSON here..."
            autosize={{ minRows: 18, maxRows: 18 }}
            style={{ fontFamily: 'monospace', fontSize: 12 }}
          />
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        {parsedPatch.error ? (
          <Alert
            type="error"
            showIcon
            message="Invalid JSON"
            description={parsedPatch.error}
          />
        ) : (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Tag color={preview.changes.length ? 'green' : 'default'}>
              <Icon type="edit" /> {preview.changes.length} matched updates
            </Tag>
            <Tag color={preview.misses.length ? 'orange' : 'default'}>
              <Icon type="warning" /> {preview.misses.length} unmatched entries
            </Tag>
            {preview.changes.slice(0, 6).map((change, index) => (
              <Tag key={`${change.scene}-${change.shot}-${index}`}>
                Scene {change.scene}, Shot {change.shot}:{' '}
                {change.fields.join(', ')}
              </Tag>
            ))}
            {preview.changes.length > 6 && (
              <Tag>+{preview.changes.length - 6} more</Tag>
            )}
          </div>
        )}
        {preview.misses.length > 0 && !parsedPatch.error && (
          <div style={{ marginTop: 8, color: '#8c6d1f', fontSize: 12 }}>
            Unmatched: {preview.misses.slice(0, 5).join(', ')}
            {preview.misses.length > 5 ? '...' : ''}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default LocalShotJsonPatchModal
